// HSE — Scheda documenti di un singolo lavoratore
import { useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Upload, Download, Send, Plus, User,
  FileText, AlertTriangle, X, FilePlus,
} from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { formatDate } from '../../lib/utils'
import {
  useUploadCertification,
  useDownloadCertification,
  type Certification,
} from '../../hooks/useHse'
import { SendDocumentsModal, type DocToSend } from './components/SendDocumentsModal'
import api from '../../lib/api'

// ─── Tipi documento (D.Lgs. 81/08) ───────────────────────────────────────────

const CERT_TYPES = [
  { value: 'formazione_generale',   label: 'Formazione sicurezza generale' },
  { value: 'formazione_specifica',  label: 'Formazione sicurezza specifica' },
  { value: 'primo_soccorso',        label: 'Attestato primo soccorso' },
  { value: 'antincendio',           label: 'Attestato antincendio' },
  { value: 'idoneita_sanitaria',    label: 'Idoneità sanitaria' },
  { value: 'documento_identita',    label: 'Documento di identità' },
  { value: 'codice_fiscale',        label: 'Tessera codice fiscale' },
  { value: 'permesso_soggiorno',    label: 'Permesso di soggiorno' },
  { value: 'patente_guida',         label: 'Patente di guida' },
  { value: 'patente_muletto',       label: 'Abilitazione muletto' },
  { value: 'lavori_quota',          label: 'Lavori in quota' },
  { value: 'contratto_lavoro',      label: 'Contratto di lavoro' },
  { value: 'consegna_dpi',          label: 'Consegna DPI' },
  { value: 'tesserino_riconoscimento', label: 'Tesserino riconoscimento (art.26)' },
]

// ─── Sotto-componente: riga certificazione ────────────────────────────────────

function CertRow({
  cert,
  isSelected,
  onToggle,
}: {
  cert: Certification
  isSelected: boolean
  onToggle: (doc: DocToSend, checked: boolean) => void
}) {
  const uploadMutation = useUploadCertification(cert.id)
  const downloadMutation = useDownloadCertification()
  const fileRef = useRef<HTMLInputElement>(null)
  const hasFile = Boolean(cert.filePath)

  const now = new Date()
  const expiry = cert.expiryDate ? new Date(cert.expiryDate) : null
  const soon = expiry ? new Date(expiry.getTime() - 30 * 86400000) : null
  const status = !expiry ? 'valid' : expiry < now ? 'expired' : soon && soon < now ? 'expiring' : 'valid'

  const label = CERT_TYPES.find((t) => t.value === cert.type)?.label ?? cert.type

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 sm:flex-row sm:items-center sm:gap-4">

      {/* Checkbox (solo se ha file) */}
      {hasFile && (
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 rounded border-[var(--fc-border)] accent-[var(--fc-primary)]"
          checked={isSelected}
          onChange={(e) =>
            onToggle(
              { type: 'certification', id: cert.id, fileName: cert.fileName ?? '', label },
              e.target.checked,
            )
          }
        />
      )}

      {/* Nome tipo */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[var(--fc-foreground)] truncate">{label}</p>
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          {cert.issueDate ? `Emesso: ${formatDate(cert.issueDate, 'dd/MM/yyyy')}` : ''}
          {cert.issueDate && cert.expiryDate ? ' · ' : ''}
          {cert.expiryDate ? `Scade: ${formatDate(cert.expiryDate, 'dd/MM/yyyy')}` : 'Nessuna scadenza'}
        </p>
      </div>

      {/* Badge stato */}
      <Badge variant={status === 'valid' ? 'success' : status === 'expiring' ? 'warning' : 'danger'}>
        {status === 'valid' ? 'Valido' : status === 'expiring' ? 'In scadenza' : 'Scaduto'}
      </Badge>

      {/* Upload / Download */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) uploadMutation.mutate(file)
          e.target.value = ''
        }}
      />

      {!hasFile ? (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
        >
          <Upload className="h-3 w-3" />
          {uploadMutation.isPending ? 'Caricamento…' : 'Carica file'}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
            title="Sostituisci"
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={async () => {
              const r = await downloadMutation.mutateAsync(cert.id)
              window.open(r.url, '_blank')
            }}
            disabled={downloadMutation.isPending}
            className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
            title={cert.fileName}
          >
            <Download className="h-3 w-3" />
            {cert.fileName
              ? cert.fileName.slice(0, 20) + (cert.fileName.length > 20 ? '…' : '')
              : 'Scarica'}
          </button>
        </div>
      )}
    </li>
  )
}

// ─── Modal aggiungi certificazione ───────────────────────────────────────────

function NewCertModal({
  technicianId,
  onClose,
}: {
  technicianId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [type, setType] = useState('formazione_generale')
  const [issueDate, setIssueDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/hse/certifications', {
        technicianId,
        type,
        issueDate: issueDate || undefined,
        expiryDate: expiryDate || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hse', 'certifications', technicianId] })
      void qc.invalidateQueries({ queryKey: ['hse', 'lavoratori'] })
      onClose()
    },
    onError: (err: Error) => setError(err.message || 'Errore'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-[var(--fc-surface)] border border-[var(--fc-border)] shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--fc-foreground)] flex items-center gap-2">
            <FilePlus className="h-5 w-5 text-[var(--fc-primary)]" />
            Nuovo documento
          </h2>
          <button onClick={onClose} className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">Tipo documento</label>
          <Select options={CERT_TYPES} value={type} onValueChange={setType} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Data emissione" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          <Input label="Data scadenza" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        </div>

        {error && (
          <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>
            Aggiungi
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export function LavoratorePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [selectedDocs, setSelectedDocs] = useState<DocToSend[]>([])
  const [showSend, setShowSend] = useState(false)
  const [showNewCert, setShowNewCert] = useState(false)

  const { data: technician } = useQuery({
    queryKey: ['technician', id],
    queryFn: async () => (await api.get<{ id: string; firstName: string; lastName: string; email: string; role: string }>(`/technicians/${id}`)).data,
    enabled: Boolean(id),
  })

  const { data: certs = [], isLoading } = useQuery({
    queryKey: ['hse', 'certifications', id],
    queryFn: async () =>
      (await api.get<Certification[]>('/hse/certifications', { params: { technicianId: id } })).data,
    enabled: Boolean(id),
  })

  const toggleDoc = (doc: DocToSend, checked: boolean) => {
    setSelectedDocs((prev) =>
      checked ? [...prev.filter((d) => d.id !== doc.id), doc] : prev.filter((d) => d.id !== doc.id),
    )
  }

  const now = new Date()
  const expired = certs.filter((c) => c.expiryDate && new Date(c.expiryDate) < now)
  const expiring = certs.filter((c) => {
    if (!c.expiryDate) return false
    const exp = new Date(c.expiryDate)
    return exp >= now && new Date(exp.getTime() - 30 * 86400000) <= now
  })
  const valid = certs.filter((c) => {
    if (!c.expiryDate) return true
    const exp = new Date(c.expiryDate)
    return exp >= now && new Date(exp.getTime() - 30 * 86400000) > now
  })

  const fullName = technician ? `${technician.firstName} ${technician.lastName}` : '…'

  return (
    <div className="space-y-5">
      <PageHeader
        title={fullName}
        subtitle={technician?.email}
        breadcrumb={[
          { label: 'HSE', href: '/hse' },
          { label: 'Lavoratori', href: '/hse/lavoratori' },
          { label: fullName },
        ]}
        actions={
          <div className="flex gap-2">
            {selectedDocs.length > 0 && (
              <Button leftIcon={<Send className="h-4 w-4" />} onClick={() => setShowSend(true)}>
                Invia selezionati ({selectedDocs.length})
              </Button>
            )}
            <Button
              variant="secondary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowNewCert(true)}
            >
              Aggiungi documento
            </Button>
          </div>
        }
      />

      {/* KPI inline */}
      {certs.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {expired.length > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {expired.length} scaduт{expired.length === 1 ? 'o' : 'i'}
            </span>
          )}
          {expiring.length > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-yellow-50 border border-yellow-200 px-3 py-1 text-xs font-medium text-yellow-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {expiring.length} in scadenza
            </span>
          )}
          <span className="flex items-center gap-1.5 rounded-full bg-[var(--fc-surface-raised)] border border-[var(--fc-border)] px-3 py-1 text-xs text-[var(--fc-foreground-muted)]">
            <FileText className="h-3.5 w-3.5" />
            {certs.length} document{certs.length === 1 ? 'o' : 'i'} totali
          </span>
        </div>
      )}

      {/* Lista documenti */}
      {isLoading && (
        <p className="text-sm text-[var(--fc-foreground-muted)]">Caricamento…</p>
      )}

      {!isLoading && certs.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--fc-border)] p-10 text-center space-y-3">
          <User className="mx-auto h-10 w-10 text-[var(--fc-foreground-subtle)]" />
          <p className="text-sm text-[var(--fc-foreground-muted)]">
            Nessun documento registrato per questo lavoratore.
          </p>
          <Button
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setShowNewCert(true)}
          >
            Aggiungi il primo documento
          </Button>
        </div>
      )}

      {[
        { label: 'Scaduti', items: expired },
        { label: 'In scadenza', items: expiring },
        { label: 'Validi', items: valid },
      ]
        .filter((g) => g.items.length > 0)
        .map((group) => (
          <section key={group.label} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--fc-foreground-muted)]">
              {group.label}
            </h3>
            <ul className="space-y-2">
              {group.items.map((cert) => (
                <CertRow
                  key={cert.id}
                  cert={cert}
                  isSelected={selectedDocs.some((d) => d.id === cert.id)}
                  onToggle={toggleDoc}
                />
              ))}
            </ul>
          </section>
        ))}

      {showSend && (
        <SendDocumentsModal
          isOpen
          onClose={() => { setShowSend(false); setSelectedDocs([]) }}
          preselected={selectedDocs}
        />
      )}

      {showNewCert && id && (
        <NewCertModal technicianId={id} onClose={() => setShowNewCert(false)} />
      )}
    </div>
  )
}
